import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { LottieLoading } from "../../components/graphics/LottieLoading";
import {
  SupportStatus,
  SupportStatusColor,
  SupportStatusItem,
  useAddSupportStatusMutation,
  useConnectCalendlyMutation,
  useDeleteSupportStatusMutation,
  useDisconnectCalendlyMutation,
  useGetCalendlyQuery,
  useGetSupportStatusesQuery,
  useReorderSupportStatusesMutation,
  useUpdateSupportStatusMutation,
} from "../../slices/SupportSlice";
import { EmailTemplatesCard } from "./EmailTemplatesCard";
import { SignaturesCard } from "./SignaturesCard";
import {
  COLOR_CHOICES,
  errorText,
  moveId,
  PILL_CLASSES,
  STATUS_CHOICES,
  statusNameProblem,
  statusPill,
  toneOf,
} from "./support.utils";
import { PAGE, PAGE_INNER, PRIMARY_BUTTON, SMALL_BUTTON } from "./supportUi";

const CARD = "rounded-[14px] border border-[#EDEFF3] bg-white";
const INPUT =
  "h-9 min-w-0 rounded-lg border border-[#DCE2EA] px-3 text-[13.5px] text-[#1F2433] outline-none focus:border-[#6B4EFF]";

const DEFAULT_COLOR: Record<string, SupportStatusColor> = {
  open: "blue",
  waiting: "yellow",
  solved: "green",
  closed: "grey",
};

/**
 * Help desk › Settings (gear, owner 2026-10-26): the owner's own case statuses (each inside a main status, so the
 * tabs, reminder drafts and the 7-day close keep working), email signatures and the Calendly connection. Separate from the app's
 * Settings page, which customers see.
 */
export const HelpDeskSettingsPage = () => {
  const { data, isLoading, isError } = useGetSupportStatusesQuery();
  return (
    <div className={PAGE}>
      <div className={classNames(PAGE_INNER, "max-w-[860px]")}>
        <Link
          to={"/support"}
          className={
            "text-[13px] font-semibold text-[#6B7185] hover:text-[#6B4EFF]"
          }>
          ‹ Help desk
        </Link>
        <h1
          className={
            "mb-1 mt-2 text-[24px] font-extrabold tracking-[-0.4px] text-[#1F2433]"
          }>
          Help desk settings
        </h1>
        <p className={"mb-5 text-[14px] text-[#5D6475]"}>
          Only for the Help desk. Your customers never see these.
        </p>
        <SignaturesCard />
        <EmailTemplatesCard />
        <CalendlyCard />
        {isLoading ? (
          <LottieLoading />
        ) : isError ? (
          <div className={classNames(CARD, "px-4 py-10 text-center text-sm")}>
            The settings couldn&apos;t be loaded. Please refresh the page.
          </div>
        ) : !data?.available ? (
          <div
            className={classNames(
              CARD,
              "px-4 py-10 text-center text-sm text-[#5D6475]",
            )}>
            Your own statuses need a database update first.
          </div>
        ) : (
          <>
            <CaseStatusesCard items={data.caseStatuses} />
          </>
        )}
      </div>
    </div>
  );
};

const SectionTitle = ({
  title,
  text,
}: {
  title: string;
  text: React.ReactNode;
}) => (
  <div className={"border-b border-[#EDEFF3] px-[18px] py-4"}>
    <h2 className={"text-[17px] font-extrabold text-[#1F2433]"}>{title}</h2>
    <p className={"mt-1 text-[13.5px] leading-relaxed text-[#5D6475]"}>
      {text}
    </p>
  </div>
);

const CaseStatusesCard = ({ items }: { items: SupportStatusItem[] }) => {
  const [reorder] = useReorderSupportStatusesMutation();
  const move = (group: SupportStatus, id: string, by: -1 | 1) => {
    const inGroup = items.filter((s) => s.group === group).map((s) => s.id);
    const moved = moveId(inGroup, id, by);
    // The server orders the whole list: other groups keep their places.
    const ids = STATUS_CHOICES.flatMap((g) =>
      g === group ? moved : items.filter((s) => s.group === g).map((s) => s.id),
    );
    reorder({ kind: "case", ids })
      .unwrap()
      .catch((e) =>
        toast(errorText(e, "Couldn't move it."), { type: "error" }),
      );
  };
  return (
    <section className={classNames(CARD, "mb-5")} data-testid={"case-statuses"}>
      <SectionTitle
        title={"Case statuses"}
        text={
          <>
            Add your own statuses inside a main status. A case set to
            &ldquo;Waiting on accountant&rdquo; is still in the <b>Waiting</b>{" "}
            tab, so the reminder drafts and the 7-day close keep working.
          </>
        }
      />
      {STATUS_CHOICES.map((group) => {
        const main = statusPill(group);
        const own = items.filter((s) => s.group === group);
        return (
          <div
            key={group}
            className={
              "border-b border-[#EDEFF3] px-[18px] py-3 last:border-0"
            }>
            <div className={"flex items-center gap-2"}>
              <span
                className={classNames(
                  "rounded-full px-2.5 py-0.5 text-[12px] font-bold",
                  PILL_CLASSES[main.tone],
                )}>
                {main.text}
              </span>
              <span className={"text-[12.5px] text-[#8A90A2]"}>
                main status · can&apos;t be changed
              </span>
            </div>
            <div className={"ml-4 mt-1.5 flex flex-col gap-1.5"}>
              {own.map((item, i) => (
                <StatusRow
                  key={item.id}
                  item={item}
                  others={items
                    .filter((s) => s.id !== item.id)
                    .map((s) => s.name)}
                  first={i === 0}
                  last={i === own.length - 1}
                  onMove={(by) => move(group, item.id, by)}
                  inUseText={(n) => `${n} case${n === 1 ? "" : "s"}`}
                  deleteText={`Its cases will show “${main.text}”.`}
                />
              ))}
              <AddStatus
                kind={"case"}
                group={group}
                others={items.map((s) => s.name)}
                placeholder={
                  group === "waiting"
                    ? "e.g. Waiting on accountant"
                    : group === "open"
                    ? "e.g. In progress"
                    : group === "solved"
                    ? "e.g. Fixed in next update"
                    : "e.g. Duplicate"
                }
              />
            </div>
          </div>
        );
      })}
    </section>
  );
};

const ColorSelect = ({
  value,
  onChange,
  disabled,
}: {
  value: SupportStatusColor;
  onChange: (c: SupportStatusColor) => void;
  disabled?: boolean;
}) => (
  <select
    aria-label={"Color"}
    value={value}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value as SupportStatusColor)}
    className={classNames(
      "h-9 cursor-pointer rounded-lg border border-[#DCE2EA] px-2 text-[13px] font-semibold",
      PILL_CLASSES[toneOf(value)],
    )}>
    {COLOR_CHOICES.map((c) => (
      <option key={c.value} value={c.value}>
        {c.label}
      </option>
    ))}
  </select>
);

const StatusRow = ({
  item,
  others,
  first,
  last,
  onMove,
  inUseText,
  deleteText,
  canDelete = true,
}: {
  item: SupportStatusItem;
  others: string[];
  first: boolean;
  last: boolean;
  onMove: (by: -1 | 1) => void;
  inUseText: (n: number) => string;
  deleteText: string;
  canDelete?: boolean;
}) => {
  const [name, setName] = useState(item.name);
  const [asking, setAsking] = useState(false);
  const [update, { isLoading }] = useUpdateSupportStatusMutation();
  const [remove, { isLoading: isRemoving }] = useDeleteSupportStatusMutation();
  useEffect(() => setName(item.name), [item.name]);
  const problem = statusNameProblem(name, item.kind, others);
  const save = (body: {
    name?: string;
    color?: SupportStatusColor;
    group?: SupportStatus;
  }) =>
    update({ id: item.id, ...body })
      .unwrap()
      .catch((e) => {
        setName(item.name);
        toast(errorText(e, "Couldn't save it."), { type: "error" });
      });
  const saveName = () => {
    if (name.trim() === item.name || problem) {
      if (problem) setName(item.name);
      return;
    }
    void save({ name });
  };
  return (
    <div
      className={
        "flex flex-wrap items-center gap-2 rounded-lg bg-[#FAFAFC] px-2 py-1.5"
      }
      data-testid={"status-row"}>
      <input
        aria-label={"Status name"}
        value={name}
        maxLength={40}
        onChange={(e) => setName(e.target.value)}
        onBlur={saveName}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") setName(item.name);
        }}
        className={classNames(INPUT, "w-[220px] flex-1 bg-white")}
      />
      <ColorSelect
        value={item.color}
        disabled={isLoading}
        onChange={(color) => void save({ color })}
      />
      {item.kind === "case" && (
        <select
          aria-label={"Main status"}
          value={item.group || "open"}
          disabled={isLoading}
          onChange={(e) =>
            void save({ group: e.target.value as SupportStatus })
          }
          className={
            "h-9 cursor-pointer rounded-lg border border-[#DCE2EA] bg-white px-2 text-[13px]"
          }>
          {STATUS_CHOICES.map((g) => (
            <option key={g} value={g}>
              in {statusPill(g).text}
            </option>
          ))}
        </select>
      )}
      <span className={"w-[64px] text-[12px] text-[#8A90A2]"}>
        {inUseText(item.inUse)}
      </span>
      <button
        type={"button"}
        aria-label={"Move up"}
        disabled={first}
        onClick={() => onMove(-1)}
        className={classNames(SMALL_BUTTON, "px-2 disabled:opacity-40")}>
        ↑
      </button>
      <button
        type={"button"}
        aria-label={"Move down"}
        disabled={last}
        onClick={() => onMove(1)}
        className={classNames(SMALL_BUTTON, "px-2 disabled:opacity-40")}>
        ↓
      </button>
      {canDelete &&
        (asking ? (
          <span className={"flex items-center gap-1.5 text-[12.5px]"}>
            {item.inUse > 0 && (
              <span className={"text-[#5D6475]"}>{deleteText}</span>
            )}
            <button
              type={"button"}
              disabled={isRemoving}
              className={classNames(SMALL_BUTTON, "text-[#B42318]")}
              onClick={() =>
                remove(item.id)
                  .unwrap()
                  .catch((e) =>
                    toast(errorText(e, "Couldn't delete it."), {
                      type: "error",
                    }),
                  )
              }>
              Yes, delete
            </button>
            <button
              type={"button"}
              className={SMALL_BUTTON}
              onClick={() => setAsking(false)}>
              Keep
            </button>
          </span>
        ) : (
          <button
            type={"button"}
            className={classNames(SMALL_BUTTON, "text-[#B42318]")}
            onClick={() => setAsking(true)}>
            Delete
          </button>
        ))}
      {problem && name.trim() !== item.name && (
        <div className={"w-full text-[12px] text-[#B42318]"}>{problem}</div>
      )}
    </div>
  );
};

const AddStatus = ({
  kind,
  group,
  others,
  placeholder,
}: {
  kind: "case";
  group?: SupportStatus;
  others: string[];
  placeholder: string;
}) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [color, setColor] = useState<SupportStatusColor>(
    (group && DEFAULT_COLOR[group]) || "blue",
  );
  const [add, { isLoading }] = useAddSupportStatusMutation();
  const problem = statusNameProblem(name, kind, others);
  if (!open)
    return (
      <button
        type={"button"}
        onClick={() => setOpen(true)}
        className={
          "self-start py-1 text-[13px] font-semibold text-[#6B4EFF] hover:underline"
        }>
        + Add a status
      </button>
    );
  const submit = () =>
    add({ kind, group, name, color })
      .unwrap()
      .then(() => {
        setName("");
        setOpen(false);
      })
      .catch((e) => toast(errorText(e, "Couldn't add it."), { type: "error" }));
  return (
    <div className={"flex flex-wrap items-center gap-2 py-1"}>
      <input
        autoFocus
        aria-label={"New status name"}
        value={name}
        maxLength={40}
        placeholder={placeholder}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !problem) void submit();
          if (e.key === "Escape") setOpen(false);
        }}
        className={classNames(INPUT, "w-[220px] flex-1")}
      />
      <ColorSelect value={color} onChange={setColor} />
      <button
        type={"button"}
        disabled={!!problem || isLoading}
        title={problem || undefined}
        onClick={() => void submit()}
        className={PRIMARY_BUTTON}>
        {isLoading ? "Adding…" : "Add"}
      </button>
      <button
        type={"button"}
        className={SMALL_BUTTON}
        onClick={() => {
          setName("");
          setOpen(false);
        }}>
        Cancel
      </button>
      {problem && name.trim() && (
        <div className={"w-full text-[12px] text-[#B42318]"}>{problem}</div>
      )}
    </div>
  );
};

/** Calendly for demo bookings (owner 2026-10-26): the owner pastes a personal access token once; it isn't saved. */
const CalendlyCard = () => {
  const { data } = useGetCalendlyQuery();
  const [connect, { isLoading }] = useConnectCalendlyMutation();
  const [disconnect, { isLoading: isDisconnecting }] =
    useDisconnectCalendlyMutation();
  const [token, setToken] = useState("");
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState(false);
  if (!data) return null;
  return (
    <section className={classNames(CARD, "mb-5")} data-testid={"calendly-card"}>
      <SectionTitle
        title={"Demos from Calendly"}
        text={
          <>
            Every demo booked in Calendly becomes a client and a &ldquo;Demo
            –&rdquo; work item in your firm. Only Calendly events with &ldquo;
            {data.demoWord}&rdquo; in their name are used.
          </>
        }
      />
      <div className={"px-[18px] py-4 text-[13.5px] text-[#3A4256]"}>
        {!data.available ? (
          <div className={"text-[#5D6475]"}>
            Demos need a database update first.
          </div>
        ) : data.connected ? (
          <div className={"flex flex-wrap items-center gap-3"}>
            <span
              className={classNames(
                "rounded-full px-2.5 py-0.5 text-[12px] font-bold",
                PILL_CLASSES.green,
              )}>
              Connected
            </span>
            <span>
              {data.accountName}
              {data.lastEventAt
                ? ` · last booking ${new Date(
                    data.lastEventAt,
                  ).toLocaleString()}`
                : " · no bookings received yet"}
            </span>
            <span className={"flex-1"} />
            {asking ? (
              <>
                <button
                  type={"button"}
                  disabled={isDisconnecting}
                  className={classNames(SMALL_BUTTON, "text-[#B42318]")}
                  onClick={() =>
                    disconnect()
                      .unwrap()
                      .catch((e) =>
                        toast(errorText(e, "Couldn't disconnect."), {
                          type: "error",
                        }),
                      )
                  }>
                  Yes, disconnect
                </button>
                <button
                  type={"button"}
                  className={SMALL_BUTTON}
                  onClick={() => setAsking(false)}>
                  Keep
                </button>
              </>
            ) : (
              <button
                type={"button"}
                className={SMALL_BUTTON}
                onClick={() => setAsking(true)}>
                Disconnect
              </button>
            )}
            {data.lastError && (
              <div className={"w-full text-[12.5px] text-[#B42318]"}>
                {data.lastError}
              </div>
            )}
          </div>
        ) : !open ? (
          <button
            type={"button"}
            className={PRIMARY_BUTTON}
            onClick={() => setOpen(true)}>
            Connect Calendly
          </button>
        ) : (
          <div className={"flex flex-col gap-3"}>
            <ol className={"list-decimal space-y-1 pl-5 leading-relaxed"}>
              <li>
                In Calendly, open <b>Integrations &amp; apps</b> &rsaquo;{" "}
                <b>API and webhooks</b>.
              </li>
              <li>
                Under <b>Personal access tokens</b>, click{" "}
                <b>Generate new token</b>, name it &ldquo;ProCharted&rdquo; and
                copy it.
              </li>
              <li>Paste it here and click Connect.</li>
            </ol>
            <div className={"text-[12.5px] text-[#5D6475]"}>
              The token is used once to tell Calendly where to send new
              bookings; ProCharted doesn&apos;t keep it. Calendly needs a paid
              plan (Standard or higher) for this.
            </div>
            <div className={"flex flex-wrap items-center gap-2"}>
              <input
                type={"password"}
                autoComplete={"off"}
                aria-label={"Calendly personal access token"}
                value={token}
                placeholder={"Paste the token"}
                onChange={(e) => setToken(e.target.value)}
                className={classNames(INPUT, "w-[320px] flex-1")}
              />
              <button
                type={"button"}
                disabled={token.trim().length < 20 || isLoading}
                className={PRIMARY_BUTTON}
                onClick={() =>
                  connect(token.trim())
                    .unwrap()
                    .then(() => {
                      setToken("");
                      setOpen(false);
                      toast("Calendly connected.", { type: "success" });
                    })
                    .catch((e) =>
                      toast(errorText(e, "Couldn't connect Calendly."), {
                        type: "error",
                      }),
                    )
                }>
                {isLoading ? "Connecting…" : "Connect"}
              </button>
              <button
                type={"button"}
                className={SMALL_BUTTON}
                onClick={() => {
                  setToken("");
                  setOpen(false);
                }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
